'use client';

import { useEffect, useRef } from 'react';
import type { AssistantAction } from '@/types/assistant';
import {
  assistantActionKey,
  useAssistantActionsStore,
} from './assistant-bridge';

/**
 * Publish the handler of a regular button so an assistant `action_card` for
 * the same record calls exactly it (ADR-001 §5). `scopeId` is the record the
 * button acts on: the design for a render, estimate or album order, the
 * estimate for a regeneration. Pass `null` while the button has nothing to act
 * on; a card then waits.
 *
 * The latest handler is always the one called, so a handler that closes over
 * fresh render state needs no memoising by the caller.
 */
export function useAssistantAction(
  action: AssistantAction,
  scopeId: string | null | undefined,
  handler: () => void | Promise<void>
): void {
  const latest = useRef(handler);
  useEffect(() => {
    latest.current = handler;
  });

  useEffect(() => {
    if (!scopeId) return;
    const key = assistantActionKey(action, scopeId);
    const stable = () => latest.current();
    const { register, unregister } = useAssistantActionsStore.getState();
    register(key, stable);
    return () => unregister(key, stable);
  }, [action, scopeId]);
}
